import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Background, Button, ButtonSize } from '../../components';
import { CheckIcon, SpinnerIcon } from './icons';
import { Features } from './components';
import {
  STRIPE_PROFFESIONAL_ANNUAL_PLAN,
  STRIPE_PROFFESIONAL_MONTHLY_PLAN,
} from '../../constants/config';
import { useMixPanel } from '../../components/MixPanel/useMixPanel';
import { useGoogleAnalytics } from '../../components/GoogleAnalytics/useGoogleAnalytics';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  getTrialUsedStatus,
  sendSetSelectedPlan,
} from '../../store/auth/auth.thunks';
import { updateSelectedPlan } from '../../store/auth/auth.slice';
import { ROUTES } from '../../routes';
import { withPageTransition } from '../../hoc';
import { UserSignUpFlow } from '../../store/auth/auth.types';
import { MixpanelEvent } from '../../components/MixPanel/events/events';
import SsuLogoutFooter from '../../components/SsuLogoutFooter/SsuLogoutFooter';
import { useWindowSize } from '../../hooks';

const Pricing: React.FC = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const userSignUpFlow = useAppSelector((state) => state.auth.userSignUpFlow);

  const [isAnnuallySelected, setIsAnnuallySelected] = useState(true);

  const history = useHistory();

  const { trackEvent, isMixPanelInitialized } = useMixPanel();
  const { trackGAEvent, isGoogleAnalyticsInitialized } = useGoogleAnalytics();

  const { width } = useWindowSize();
  const isDesktopView = width > 1024;

  const handleEnterprisePlanButtonClick = () => {
    trackEvent(MixpanelEvent.PRICING_PAGE_CHOOSED_TALK_TO_SALES);
    window.open('https://www.substrata.me/get-a-demo/', '_blank');
  };

  const attrs = {
    container: {
      className: 'pricing',
    },
    content: {
      className: 'pricing__content',
    },
    title: {
      className: 'pricing__title',
    },
    header: {
      className: 'pricing__header',
    },
    tabsWrapper: {
      className: 'pricing__tabs-wrapper',
    },
    monthlyTab: {
      type: 'button' as const,
      disabled: !isAnnuallySelected,
      className: 'pricing__tab',
      onClick: () => {
        setIsAnnuallySelected(false);
        trackEvent(MixpanelEvent.PRICING_PAGE_CHOOSED_MONTHLY_PAY);
      },
    },
    annuallyTab: {
      type: 'button' as const,
      disabled: isAnnuallySelected,
      className: 'pricing__tab',
      onClick: () => {
        setIsAnnuallySelected(true);
        trackEvent(MixpanelEvent.PRICING_PAGE_CHOOSED_YEARLY_PAY);
      },
    },
    plansWrapper: {
      className: 'pricing__plans-wrapper',
    },
    plan: {
      className: 'pricing__plan',
    },
    planHeader: {
      className: 'pricing__plan-header',
    },
    planName: {
      className: 'pricing__plan-name',
    },
    planDescription: {
      className: 'pricing__plan-description',
    },
    planPrice: {
      className: 'pricing__plan-price',
    },
    currencySign: {
      className: 'pricing__currency-sign',
    },
    discountDisclaimer: {
      className: 'pricing__discount-disclaimer',
    },
    planPriceDisclaimer: {
      className: 'pricing__plan-price-disclaimer',
    },
    professionalPlanButton: {
      wrapper: {
        className: 'pricing__plan-button-wrapper',
      },
      button: {
        size: ButtonSize.S,
        disabled: !user,
        className: 'pricing__plan-button',
        onClick: () => {
          dispatch(sendSetSelectedPlan());
          dispatch(
            updateSelectedPlan({
              stripePlanInfo: isAnnuallySelected
                ? STRIPE_PROFFESIONAL_ANNUAL_PLAN
                : STRIPE_PROFFESIONAL_MONTHLY_PLAN,
              isAnnual: isAnnuallySelected,
              usersCount: 1,
              name: 'Professional',
            })
          );

          trackEvent(MixpanelEvent.PRICING_PAGE_CHOOSED_PROFESSIONAL_PLAN);
          trackGAEvent('event', 'signup.pricing-page.subscribe-clicked');

          history.push(ROUTES.PAYMENT_DETAILS);
        },
      },
      trialText: {
        className: 'pricing__plan-button-trial-text',
      },
    },
    spinnerIcon: {
      className: 'pricing__spinner-icon',
    },
    enterprisePlanButton: {
      size: ButtonSize.S,
      onClick: handleEnterprisePlanButtonClick,
      className: 'pricing__plan-button',
      style: {
        marginBottom: '24px',
      },
    },
    planFeaturesWrapper: {
      className: 'pricing__plan-features-wrapper',
    },
    planFeatureTitle: {
      className: 'pricing__plan-feature-title',
    },
    planFeature: {
      className: 'pricing__plan-feature',
    },
  };

  const professionalPrice = useMemo(() => {
    const props = {
      container: {
        key: Date.now(),
        className: 'pricing__plan-price',
      },
      currencySign: {
        className: 'pricing__currency-sign',
      },
      discountDisclaimer: {
        className: 'pricing__discount-disclaimer',
      },
    };

    const dollarSign = (
      <svg
        width="13"
        height="20"
        viewBox="0 0 13 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.378 13C12.378 14.1467 11.958 15.1 11.118 15.86C10.278 16.6067 9.14464 17.06 7.71797 17.22V19.26H5.49797V17.22C3.69797 16.9933 2.11797 16.36 0.757969 15.32L1.97797 13.56C2.56464 14.0533 3.2713 14.44 4.09797 14.72C4.93797 15 5.7913 15.14 6.65797 15.14C7.6713 15.14 8.48464 14.9533 9.09797 14.58C9.7113 14.1933 10.018 13.6867 10.018 13.06C10.018 12.4867 9.73797 12.0333 9.17797 11.7C8.61797 11.3667 7.7313 11.12 6.51797 10.96C4.6113 10.6933 3.2313 10.2267 2.37797 9.56C1.52464 8.89333 1.09797 7.94667 1.09797 6.72C1.09797 5.69333 1.4913 4.82667 2.27797 4.12C3.06464 3.4 4.1113 2.95333 5.41797 2.78V0.82H7.61797V2.8C9.17797 3.01333 10.538 3.56667 11.698 4.46L10.518 6.12C9.34464 5.22667 8.01797 4.78 6.53797 4.78C5.61797 4.78 4.86464 4.96667 4.27797 5.34C3.6913 5.7 3.39797 6.16667 3.39797 6.74C3.39797 7.3 3.66464 7.74667 4.19797 8.08C4.7313 8.41333 5.58464 8.66667 6.75797 8.84C8.74464 9.13333 10.178 9.60667 11.058 10.26C11.938 10.9 12.378 11.8133 12.378 13Z"
          fill="#070B12"
        />
      </svg>
    );

    if (isAnnuallySelected) {
      return (
        <span {...props.container}>
          <span {...props.currencySign}>{dollarSign}</span>
          <span>
            {STRIPE_PROFFESIONAL_ANNUAL_PLAN.price -
              STRIPE_PROFFESIONAL_ANNUAL_PLAN.discount}
          </span>
          <span {...props.discountDisclaimer}>Save 15%</span>
        </span>
      );
    }

    return (
      <span {...props.container}>
        <span {...props.currencySign}>{dollarSign}</span>
        <span>
          {STRIPE_PROFFESIONAL_MONTHLY_PLAN.price -
            STRIPE_PROFFESIONAL_MONTHLY_PLAN.discount}
        </span>
      </span>
    );
  }, [isAnnuallySelected]);

  const professionalPriceDescription = useMemo(() => {
    const props = {
      container: {
        key: Date.now() + 1,
        className: 'pricing__plan-price-disclaimer',
      },
    };

    if (isAnnuallySelected) {
      return <span {...props.container}>User/Year, Up to 5 Users</span>;
    }

    return <span {...props.container}>User/Month, Up to 5 Users</span>;
  }, [isAnnuallySelected]);

  useEffect(() => {
    if (isMixPanelInitialized) {
      trackEvent(MixpanelEvent.ARRIVED_TO_PRICING_PAGE);
    }
  }, [isMixPanelInitialized, trackEvent]);

  useEffect(() => {
    if (isGoogleAnalyticsInitialized) {
      trackGAEvent('pageview', 'signup.pricing-page');
    }
  }, [isGoogleAnalyticsInitialized, trackGAEvent]);

  useEffect(() => {
    if (user) {
      dispatch(getTrialUsedStatus());
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (userSignUpFlow === UserSignUpFlow.COMPLETED) {
      history.replace(ROUTES.HOME);
    }
  }, [history, userSignUpFlow]);

  return (
    <div {...attrs.container}>
      <div {...attrs.content}>
        <div {...attrs.header}>
          <span {...attrs.title}>Plans & Pricing</span>
          <div {...attrs.tabsWrapper}>
            <button {...attrs.monthlyTab}>Monthly</button>
            <button {...attrs.annuallyTab}>Annual</button>
          </div>
        </div>
        <div {...attrs.plansWrapper}>
          <div {...attrs.plan}>
            <div {...attrs.planHeader}>
              <span {...attrs.planName}>Professional</span>
              <span {...attrs.planDescription}>For B2B Dealmakers</span>
              {professionalPrice}
              {professionalPriceDescription}
            </div>
            <div {...attrs.professionalPlanButton.wrapper}>
              <Button {...attrs.professionalPlanButton.button}>
                {!user ? (
                  <SpinnerIcon {...attrs.spinnerIcon} />
                ) : (
                  <span>Continue</span>
                )}
              </Button>
              <div {...attrs.professionalPlanButton.trialText}>
                30-Day Free Trial (Credit Card Required)
              </div>
            </div>

            <div {...attrs.planFeaturesWrapper}>
              <div {...attrs.planFeatureTitle}>Features:</div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>SoundWave</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Q for Email</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Temper</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Signals</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Webhooks</span>
              </div>
            </div>
          </div>
          <div {...attrs.plan}>
            <div {...attrs.planHeader}>
              <span {...attrs.planName}>Edge</span>
              <span {...attrs.planDescription}>
                For Medium / Large Revenue Teams
              </span>
              <span {...attrs.planPrice}>
                <span>Priced to Fit</span>
              </span>
              <span {...attrs.planPriceDisclaimer}>By Quote</span>
            </div>
            <Button {...attrs.enterprisePlanButton}>
              <span>Let&apos;s talk</span>
            </Button>
            <div {...attrs.planFeaturesWrapper}>
              <div {...attrs.planFeatureTitle}>
                Everything in Professional, plus:
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Performance & Team Q</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Custom CRM & Workflow Integration</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Custom Onboarding & Training</span>
              </div>
              <div {...attrs.planFeature}>
                <CheckIcon />
                <span>Dedicated Success Manager</span>
              </div>
            </div>
          </div>
          {isDesktopView ? <Background /> : null}
        </div>
      </div>
      <Features />
      <SsuLogoutFooter />
    </div>
  );
};

export default withPageTransition(Pricing);
